// # Mentor Notes Component for Step 2 Mentorship
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import SharedHeader from '../../../components/SharedHeader';
import DashboardFooter from '../../../components/DashboardFooter';
import { db } from '../../../auth/firebase';
import { collection, doc, getDoc, updateDoc, addDoc, query, orderBy, getDocs, deleteDoc } from 'firebase/firestore';
import { IoAddOutline, IoCloseOutline, IoPencilOutline, IoTrashOutline } from 'react-icons/io5';
import './mentor_notes2.css';

// # Note Types Enum
const NOTE_TYPES = {
  MEETING: 'meeting',
  BEHAVIOUR: 'behaviour',
  COMPLIANCE: 'compliance',
  PROGRESS: 'progress'
};

function MentorNotes2({ isAdmin }) {
  const navigate = useNavigate();
  const { userProfile, user } = useAuthStore();
  const [activeTab, setActiveTab] = useState(NOTE_TYPES.MEETING);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editedContent, setEditedContent] = useState('');
  const [expandedNote, setExpandedNote] = useState(null);
  
  // # Create base path based on admin status
  const basePath = isAdmin ? '/admin/mentorship' : '/mentorship';

  // # Fetch notes based on active tab
  useEffect(() => {
    const fetchNotes = async () => {
      if (!user?.uid) return;
      
      setLoading(true);
      try {
        const menteeRef = doc(db, 's2_mentee', user.uid);
        const menteeDoc = await getDoc(menteeRef);
        
        if (menteeDoc.exists()) {
          const menteeData = menteeDoc.data();
          // # Get notes from the specific type map within mentor_notes
          const mentorNotes = menteeData.mentor_notes?.[activeTab] || {};
          
          // # Sort notes by date
          const filteredNotes = Object.entries(mentorNotes)
            .sort((a, b) => new Date(b[1].createdAt) - new Date(a[1].createdAt))
            .map(([id, note]) => ({ id, ...note }));
          
          setNotes(filteredNotes);
        }
      } catch (error) {
        console.error('Error fetching notes:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotes();
  }, [activeTab, user?.uid]);

  // # Handle adding new note
  const handleAddNote = async (e) => {
    e.preventDefault();
    if (!newNote.trim() || !user?.uid) return;

    setLoading(true);
    try {
      // # Get writer info from users collection
      const userRef = doc(db, 'users', user.uid);
      const userDoc = await getDoc(userRef);
      const userData = userDoc.data();
      
      // # Create note ID with proper format
      const timestamp = new Date().toISOString();
      const formattedDate = timestamp.replace(/[:.]/g, '-');
      const randomId = Math.random().toString(36).substr(2, 6);
      const noteId = `mntr_nt_${formattedDate}_${randomId}`;
      
      const menteeRef = doc(db, 's2_mentee', user.uid);
      const menteeDoc = await getDoc(menteeRef);
      
      if (menteeDoc.exists()) {
        const menteeData = menteeDoc.data();
        const currentMentor = menteeData.mentorship?.currentMentor;
        const previousMentors = menteeData.mentorship?.previousMentors || [];
        
        // # Update mentor assignment and add note to specific type map within mentor_notes
        const updates = {
          [`mentor_notes.${activeTab}.${noteId}`]: {
            content: newNote,
            createdAt: timestamp,
            createdBy: {
              name: `${userData.firstName} ${userData.lastName}`.trim() || 'Unknown',
              email: userData.email,
              role: userData.role || 'tutor'
            }
          },
          'mentorship.status': 'assigned',
          'mentorship.currentMentor': userData.email
        };

        // # Move current mentor to previous mentors if different
        if (currentMentor && currentMentor !== userData.email && !previousMentors.includes(currentMentor)) {
          updates['mentorship.previousMentors'] = [...previousMentors, currentMentor];
        }

        await updateDoc(menteeRef, updates);
        
        // # Refresh notes
        const updatedNotes = [
          {
            id: noteId,
            content: newNote,
            createdAt: timestamp,
            createdBy: {
              name: `${userData.firstName} ${userData.lastName}`.trim() || 'Unknown',
              email: userData.email,
              role: userData.role || 'tutor'
            }
          },
          ...notes
        ];
        
        setNotes(updatedNotes);
        setNewNote('');
        setShowNoteForm(false); // Hide form after successful submission
      }
    } catch (error) {
      console.error('Error adding note:', error);
    } finally {
      setLoading(false);
    }
  };

  // # Handle edit note
  const handleEditNote = async (noteId, content) => {
    if (!user?.uid) return;
    
    try {
      const menteeRef = doc(db, 's2_mentee', user.uid);
      await updateDoc(menteeRef, {
        [`mentor_notes.${activeTab}.${noteId}.content`]: content,
        [`mentor_notes.${activeTab}.${noteId}.lastEdited`]: new Date().toISOString()
      });
      
      // # Update notes state
      setNotes(notes.map(note => 
        note.id === noteId 
          ? { ...note, content, lastEdited: new Date().toISOString() }
          : note
      ));
      
      setEditingNote(null);
      setEditedContent('');
    } catch (error) {
      console.error('Error editing note:', error);
    }
  };

  // # Handle delete note
  const handleDeleteNote = async (noteId) => {
    if (!user?.uid || !window.confirm('Are you sure you want to delete this note?')) return;
    
    try {
      const menteeRef = doc(db, 's2_mentee', user.uid);
      await updateDoc(menteeRef, {
        [`mentor_notes.${activeTab}.${noteId}`]: null
      });
      
      // # Update notes state
      setNotes(notes.filter(note => note.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  // # Helper function to truncate text
  const truncateText = (text) => {
    const words = text.split(' ');
    if (words.length > 20 || text.length > 100) {
      const truncated = words.slice(0, 20).join(' ');
      return {
        isTruncated: true,
        text: truncated + '...'
      };
    }
    return {
      isTruncated: false,
      text
    };
  };

  // # Toggle note expansion in modal
  const toggleNoteExpansion = (note) => {
    setExpandedNote(expandedNote?.id === note.id ? null : note);
  };

  // # Close modal when clicking outside
  const handleModalClick = (e) => {
    if (e.target.classList.contains('mentor_notes2_modal_overlay')) {
      setExpandedNote(null);
    }
  };

  // # Helper function to get relative time
  const getRelativeTime = (date) => {
    const now = new Date();
    const then = new Date(date);
    const diffInSeconds = Math.floor((now - then) / 1000);
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    const diffInMonths = Math.floor(diffInDays / 30);
    const diffInYears = Math.floor(diffInDays / 365);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h ago`;
    } else if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return `${diffInDays}d ago`;
    } else if (diffInDays < 30) {
      const weeks = Math.floor(diffInDays / 7);
      return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
    } else if (diffInMonths < 12) {
      return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    } else {
      return `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
    }
  };

  return (
    <div className="mentor_notes2_container">
      <SharedHeader />
      <div className="mentor_notes2_main">
        <div className="mentor_notes2_content">
          <h1 className="mentor_notes2_title">Step 2 Mentor Notes</h1>
          
          {/* # Tabs */}
          <div className="mentor_notes2_tabs">
            <button 
              className={`mentor_notes2_tab ${activeTab === NOTE_TYPES.MEETING ? 'active' : ''}`}
              onClick={() => setActiveTab(NOTE_TYPES.MEETING)}
            >
              Meeting Notes
            </button>
            <button 
              className={`mentor_notes2_tab ${activeTab === NOTE_TYPES.BEHAVIOUR ? 'active' : ''}`}
              onClick={() => setActiveTab(NOTE_TYPES.BEHAVIOUR)}
            >
              Behaviour Notes
            </button>
            <button 
              className={`mentor_notes2_tab ${activeTab === NOTE_TYPES.COMPLIANCE ? 'active' : ''}`}
              onClick={() => setActiveTab(NOTE_TYPES.COMPLIANCE)}
            >
              Compliance Notes
            </button>
            <button 
              className={`mentor_notes2_tab ${activeTab === NOTE_TYPES.PROGRESS ? 'active' : ''}`}
              onClick={() => setActiveTab(NOTE_TYPES.PROGRESS)}
            >
              Progress Notes
            </button>
          </div>

          {/* # Add Note Button and Form */}
          <div className="mentor_notes2_add_section">
            {!showNoteForm ? (
              <button 
                className="mentor_notes2_add_button"
                onClick={() => setShowNoteForm(true)}
                title="Add new note"
              >
                <IoAddOutline size={24} />
              </button>
            ) : null}
          </div>

          {/* # Note Form when enabled */}
          {showNoteForm && (
            <form onSubmit={handleAddNote} className="mentor_notes2_form">
              <div className="mentor_notes2_form_header">
                <h3>Add New Note</h3>
                <button 
                  type="button" 
                  className="mentor_notes2_close_button"
                  onClick={() => setShowNoteForm(false)}
                >
                  <IoCloseOutline size={20} />
                </button>
              </div>
              <textarea
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Add a new note..."
                className="mentor_notes2_textarea"
                required
                autoFocus
              />
              <div className="mentor_notes2_form_actions">
                <button 
                  type="submit" 
                  className="mentor_notes2_submit"
                  disabled={loading}
                >
                  {loading ? 'Submitting...' : 'Submit Note'}
                </button>
              </div>
            </form>
          )}

          {/* # Notes List */}
          <div className="mentor_notes2_notes_list">
            {loading && !showNoteForm ? (
              <div className="mentor_notes2_loading">Loading notes...</div>
            ) : notes.length > 0 ? (
              notes.map((note) => (
                <div 
                  key={note.id} 
                  className="mentor_notes2_note"
                  data-type={activeTab}
                >
                  <div className="mentor_notes2_note_header">
                    <div className="mentor_notes2_note_author_info">
                      <span className="mentor_notes2_note_author_name">
                        {note.createdBy.name}
                      </span>
                      <span className="mentor_notes2_note_author_email">
                        {note.createdBy.email}
                      </span>
                      <span 
                        className="mentor_notes2_note_author_role"
                        data-role={note.createdBy.role}
                      >
                        {note.createdBy.role}
                      </span>
                    </div>
                    <div className="mentor_notes2_note_actions">
                      <div className="mentor_notes2_date_group">
                        <span className="mentor_notes2_note_relative_date">
                          {getRelativeTime(note.createdAt)}
                        </span>
                        <span className="mentor_notes2_note_date">
                          {new Date(note.createdAt).toLocaleString()}
                          {note.lastEdited && 
                            <span className="mentor_notes2_edit_indicator">
                              (edited {getRelativeTime(note.lastEdited)})
                            </span>
                          }
                        </span>
                      </div>
                      <button
                        className="mentor_notes2_action_button edit"
                        onClick={() => {
                          setEditingNote(note.id);
                          setEditedContent(note.content);
                        }}
                        title="Edit note"
                      >
                        <IoPencilOutline size={18} />
                      </button>
                      <button
                        className="mentor_notes2_action_button delete"
                        onClick={() => handleDeleteNote(note.id)}
                        title="Delete note"
                      >
                        <IoTrashOutline size={18} />
                      </button>
                    </div>
                  </div>
                  {editingNote === note.id ? (
                    <div className="mentor_notes2_edit_form">
                      <textarea
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        className="mentor_notes2_textarea"
                        required
                      />
                      <div className="mentor_notes2_edit_actions">
                        <button
                          className="mentor_notes2_edit_button save"
                          onClick={() => handleEditNote(note.id, editedContent)}
                        >
                          Save
                        </button>
                        <button
                          className="mentor_notes2_edit_button cancel"
                          onClick={() => {
                            setEditingNote(null);
                            setEditedContent('');
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mentor_notes2_note_content">
                      {(() => {
                        const { isTruncated, text } = truncateText(note.content);
                        return (
                          <>
                            {text}
                            {isTruncated && (
                              <button
                                className="mentor_notes2_read_more"
                                onClick={() => toggleNoteExpansion(note)}
                              >
                                Read more
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="mentor_notes2_empty">
                No {activeTab} notes found
              </div>
            )}
          </div>

          {/* # Modal for expanded note */}
          {expandedNote && (
            <div className="mentor_notes2_modal_overlay" onClick={handleModalClick}>
              <div className="mentor_notes2_modal">
                <div className="mentor_notes2_modal_header">
                  <div className="mentor_notes2_modal_header_top">
                    <h3 className="mentor_notes2_modal_title">Full Note Content</h3>
                    <button 
                      className="mentor_notes2_modal_close"
                      onClick={() => setExpandedNote(null)}
                    >
                      <IoCloseOutline size={24} />
                    </button>
                  </div>
                  <div className="mentor_notes2_modal_info">
                    <div className="mentor_notes2_modal_author">
                      <span className="mentor_notes2_modal_author_name">
                        {expandedNote.createdBy.name}
                      </span>
                      <span className="mentor_notes2_modal_author_email">
                        {expandedNote.createdBy.email}
                      </span>
                      <span 
                        className="mentor_notes2_modal_author_role"
                        data-role={expandedNote.createdBy.role}
                      >
                        {expandedNote.createdBy.role}
                      </span>
                    </div>
                    <div className="mentor_notes2_modal_dates">
                      <div className="mentor_notes2_modal_date_group">
                        <span className="mentor_notes2_modal_relative_date">
                          {getRelativeTime(expandedNote.createdAt)}
                        </span>
                        <span className="mentor_notes2_modal_date">
                          {new Date(expandedNote.createdAt).toLocaleString()}
                          {expandedNote.lastEdited && 
                            <span className="mentor_notes2_edit_indicator">
                              (edited {getRelativeTime(expandedNote.lastEdited)})
                            </span>
                          }
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mentor_notes2_modal_content">
                  {expandedNote.content}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <DashboardFooter />
    </div>
  );
}

export default MentorNotes2;
